import appConfig from 'shared/config/app.config'
import {
    API_TIMEOUT_MS,
    REQUEST_TIMEOUT_MESSAGE,
    REQUEST_HEADER_AUTH_KEY,
    TOKEN_TYPE,
} from 'shared/constants/api.constants'
import { createHttpClient } from './createHttpClient'
import { onSignOutSuccess } from 'store/auth/sessionSlice'
import { initialState, setUser } from 'store/auth/userSlice'
import store from 'app/store'
import { showNotification } from 'utils/commonUtils'

const BASE_URL = appConfig.apiPrefix

class ApiClient {
    isCheckAuth = true
    isKeepRequest = false
    accessToken = ''
    requestInstance = {}

    constructor(config) {
        this.instance = createHttpClient({ baseURL: BASE_URL, ...config })
    }

    setAuthorization = (token) => {
        this.accessToken = `${TOKEN_TYPE}${token}`
    }

    initTokenCancel = (requestId, abortInstance) => {
        this.requestInstance[requestId] = abortInstance
    }

    cancelRequest = (requestId, isTimeoutCancel) => {
        if (this.requestInstance[requestId]) {
            this.requestInstance[requestId].abort()
            delete this.requestInstance[requestId]

            if (isTimeoutCancel) {
                showErrorMessage(REQUEST_TIMEOUT_MESSAGE)
            }
        }
    }

    fetchApi = ({ method, config }) => {
        const {
            url = '',
            isKeepRequest = this.isKeepRequest,
            isCheckAuth = this.isCheckAuth,
            customHeaders = {},
            headers = {},
            isHandleError = true,
            requestId = `${url}-${new Date().getTime()}`,
            ...otherConfig
        } = config

        const controller = new AbortController()
        this.initTokenCancel(requestId, controller)

        const cancelTimeout = isKeepRequest
            ? null
            : setTimeout(() => {
                  this.cancelRequest(requestId, true)
              }, API_TIMEOUT_MS)

        return new Promise((resolve, reject) => {
            this.instance
                .request({
                    method,
                    url,
                    headers: {
                        ...(isCheckAuth && this.accessToken
                            ? {
                                  [REQUEST_HEADER_AUTH_KEY]:
                                      this.accessToken,
                              }
                            : {}),
                        ...headers,
                        ...customHeaders,
                    },
                    signal: controller.signal,
                    ...otherConfig,
                })
                .then((response) => {
                    if (response.data.success) {
                        resolve(response.data.data)
                        return
                    }

                    reject(response.data.error)
                })
                .catch((error) => {
                    if (error.code !== 'ERR_CANCELED' && isHandleError) {
                        handleError(error)
                    }

                    reject(error.response || error)
                })
                .finally(() => {
                    if (this.requestInstance[requestId]) {
                        delete this.requestInstance[requestId]
                    }

                    if (cancelTimeout) {
                        clearTimeout(cancelTimeout)
                    }
                })
        })
    }

    get = ({ url, config, params }) =>
        this.fetchApi({
            method: 'GET',
            config: {
                ...(config || {}),
                url,
                params,
            },
        })

    post = ({ url, config, data, headers }) =>
        this.fetchApi({
            method: 'POST',
            config: {
                ...(config || {}),
                url,
                data,
                headers,
            },
        })

    put = ({ url, config, data, headers }) =>
        this.fetchApi({
            method: 'PUT',
            config: {
                ...(config || {}),
                url,
                data,
                headers,
            },
        })

    patch = ({ url, config, data, headers }) =>
        this.fetchApi({
            method: 'PATCH',
            config: {
                ...(config || {}),
                url,
                data,
                headers,
            },
        })

    delete = ({ url, config, params, headers }) =>
        this.fetchApi({
            method: 'DELETE',
            config: {
                ...(config || {}),
                url,
                params,
                headers,
            },
        })

    streamFile = ({ url, config }) => {
        return this.instance.get({
            url,
            ...config,
        })
    }
}

const showErrorMessage = (message) => {
    showNotification('danger', '', message)
}

export const handleError = (error) => {
    const status = error?.response?.status

    if (status === 401 && store.getState().auth.session.signedIn) {
        store.dispatch(onSignOutSuccess())
        store.dispatch(setUser(initialState))
    }

    const message = error?.response?.data?.message
    if (message) {
        showErrorMessage(message)
    }
}

const apiClient = new ApiClient({})

export default apiClient
